import React, { useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { Table, VisibilityState } from '@tanstack/react-table';
import { translate } from '../../../app/shared/utils/localization-utils';
import { CheckboxInput } from '../Checkbox/CheckboxInput';
import { Button } from '../shadcn-components/button';
import { TableHeaderData } from '../../../app/data/types/tables/table-header';

interface TableModalProps<TData extends object> {
	table: Table<TData>;
	columnsData: TableHeaderData[];
	pendingColumnVisibility: VisibilityState;
	setPendingColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
	save: () => void;
	close: () => void;
}

export function DataTableModal<TData extends object>({
	table,
	columnsData,
	pendingColumnVisibility,
	setPendingColumnVisibility,
	save,
	close,
}: TableModalProps<TData>) {
	useEffect(() => {
		setPendingColumnVisibility(table.getState().columnVisibility);
	}, [table, setPendingColumnVisibility]);

	return (
		<div className="fixed top-0 left-0 h-screen w-screen z-20 bg-black/50 flex items-center justify-center">
			<div className="bg-white p-10 max-w-[800px] w-full">
				<div className="flex items-center justify-between">
					<p>Table options</p>
					<XIcon onClick={close} className="cursor-pointer" />
				</div>
				<div className="grid grid-cols-3 gap-x-20 py-8">
					{table.getAllColumns()
						.filter(col => col.getCanHide())
						.map((column) => {
							const colData = columnsData.find(cd => cd.key === column.id);
							const name = colData ? colData.name : column.id;

							return (
								<CheckboxInput
									key={column.id}
									text={name}
									checked={pendingColumnVisibility[column.id] !== false}
									onCheckedChange={(val) =>
										setPendingColumnVisibility(prev => ({
											...prev,
											[column.id]: !!val,
										}))
									}
									className="flex-row-reverse justify-start"
								/>
							);
						})}
				</div>
				<div className="flex items-center justify-end gap-3">
					<Button variant={'white'} onClick={close}>{translate('buttonClose')}</Button>
					<Button
						variant="black"
						onClick={() => {
							table.setColumnVisibility(pendingColumnVisibility);
							save();
						}}
					>
						{translate('buttonSave')}
					</Button>
				</div>
			</div>
		</div>
	);
}
